// ============================================================================
// CLEO'S SECRET — ConsultationQuestions
// File: src/pages/ConsultationQuestions.jsx
//
// The Stage 4 page where the patient curates the questions they will ask
// their GP at the next consultation. AI drafts a starter list, the patient
// edits / reorders / deletes / adds, and finalises the list. The finalised
// list is sent to the GP for review before the appointment.
//
// Pattern: PATIENT-FACING EDITORIAL. Cream PAP page, SAND header strip,
// reading-width body with editable question rows. Includes an obsidian
// save/finalise bar at the bottom (the only obsidian on a patient page —
// signals "this commits a clinical decision" the way the Approve button
// does on the clinician side).
//
// Already on the May 2026 design system; this pass cleans up:
//   • Inline Cinzel strings replaced with CINZEL constant
//   • Unused T import removed
//   • Sentence case throughout, em dashes swept
//   • Reorder buttons use Cinzel "UP" / "DN" / "DEL" labels instead of
//     unicode arrows for editorial register
//   • Empty state and generating state given proper editorial treatment
//
// API endpoints preserved:
//   • /api/get-reveal-state           — load stage4_questions
//   • /api/save-consultation-questions — save draft / finalise
//   • /api/draft-consultation-questions — regenerate AI list
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, FF, FS,
  BtnPrimary, BtnGhost, Spinner,
} from '../lib/design-tokens';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ═════════════════════════════════════════════════════════════════════════
// ConsultationQuestions
// ═════════════════════════════════════════════════════════════════════════
export default function ConsultationQuestions(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var firstName = props.firstName;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var questionsState = useState([]);
  var questions = questionsState[0];
  var setQuestions = questionsState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var lastSavedAtState = useState(null);
  var lastSavedAt = lastSavedAtState[0];
  var setLastSavedAt = lastSavedAtState[1];

  var generatingState = useState(false);
  var generating = generatingState[0];
  var setGenerating = generatingState[1];

  var newQuestionTextState = useState('');
  var newQuestionText = newQuestionTextState[0];
  var setNewQuestionText = newQuestionTextState[1];

  var questionsRecordIdState = useState(null);
  var questionsRecordId = questionsRecordIdState[0];
  var setQuestionsRecordId = questionsRecordIdState[1];

  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (!email) {
      if (emailWaitMs >= 3000) {
        setError('You need to be logged in to view your questions.');
        setLoading(false);
        return;
      }
      var timer = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
      return function() { clearTimeout(timer); };
    }
    loadQuestions();
  }, [email, emailWaitMs]);

  function loadQuestions() {
    setLoading(true);
    setError(null);
    fetch('/api/get-reveal-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not load questions'); });
        return res.json();
      })
      .then(function(result) {
        setData(result);
        var q = result.stage4_questions || {};
        setQuestionsRecordId(q.questionsRecordId);

        var initial = (q.finalQuestions && q.finalQuestions.length > 0)
          ? q.finalQuestions
          : (q.aiDraftedQuestions || []);

        if (q.finalizedAt) setLastSavedAt(q.finalizedAt);

        setQuestions(initial.map(function(item, i) {
          return {
            id: item.id || ('q_local_' + i),
            text: item.text || '',
            category: item.category || null,
          };
        }));
        setLoading(false);
      })
      .catch(function(err) { setError(err.message); setLoading(false); });
  }

  function saveQuestions(opts) {
    opts = opts || {};
    if (!questionsRecordId) return;
    setSaving(true);

    var cleanQuestions = questions
      .map(function(q) { return { id: q.id, text: q.text.trim(), category: q.category }; })
      .filter(function(q) { return q.text.length > 0; });

    fetch('/api/save-consultation-questions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        questionsRecordId: questionsRecordId,
        finalQuestions: cleanQuestions,
        finalize: !!opts.finalize,
      }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not save'); });
        return res.json();
      })
      .then(function(result) {
        setSaving(false);
        setLastSavedAt(result.finalizedAt || new Date().toISOString());
        if (opts.finalize) {
          setTimeout(function() { if (onNavigate) onNavigate('portal'); }, 1500);
        }
      })
      .catch(function(err) { setError(err.message); setSaving(false); });
  }

  function regenerate() {
    setGenerating(true);
    fetch('/api/draft-consultation-questions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, regenerate: true }),
    })
      .then(function(res) { return res.json(); })
      .then(function() { setGenerating(false); loadQuestions(); })
      .catch(function() { setGenerating(false); });
  }

  function updateQuestionText(id, newText) {
    setQuestions(function(prev) {
      return prev.map(function(q) {
        if (q.id === id) return Object.assign({}, q, { text: newText });
        return q;
      });
    });
  }

  function deleteQuestion(id) {
    setQuestions(function(prev) { return prev.filter(function(q) { return q.id !== id; }); });
  }

  function moveUp(idx) {
    if (idx === 0) return;
    setQuestions(function(prev) {
      var next = prev.slice();
      var temp = next[idx - 1];
      next[idx - 1] = next[idx];
      next[idx] = temp;
      return next;
    });
  }

  function moveDown(idx) {
    if (idx === questions.length - 1) return;
    setQuestions(function(prev) {
      var next = prev.slice();
      var temp = next[idx + 1];
      next[idx + 1] = next[idx];
      next[idx] = temp;
      return next;
    });
  }

  function addQuestion() {
    var text = newQuestionText.trim();
    if (!text) return;
    setQuestions(function(prev) {
      return prev.concat([{ id: 'q_local_' + Date.now(), text: text, category: null }]);
    });
    setNewQuestionText('');
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // ERROR / NOT-READY STATE
  // ────────────────────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        padding: 'clamp(80px, 12vw, 140px) clamp(24px, 3vw, 40px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Not yet ready</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.1,
            letterSpacing: '-0.014em',
            margin: '0 0 clamp(24px, 2.8vw, 36px)',
          }}>
            Your questions are not yet <span style={{ fontStyle: 'italic', color: P.BR2 }}>available.</span>
          </h1>

          <div style={{
            width: 'clamp(120px, 12vw, 160px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(28px, 3vw, 36px)',
            opacity: 0.85,
          }} />

          {error && (
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(17px, 1.6vw, 20px)',
              color: P.TL,
              lineHeight: 1.75,
              maxWidth: 500,
              margin: '0 auto clamp(36px, 4vw, 48px)',
            }}>{error}</p>
          )}

          <BtnGhost onClick={function() { if (onNavigate) onNavigate('portal'); }}>
            Back to portal
          </BtnGhost>
        </div>
      </div>
    );
  }

  var isFinalized = !!(data.stage4_questions && data.stage4_questions.finalizedAt);

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Editorial header ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(72px, 9vw, 112px) clamp(24px, 3vw, 40px) clamp(48px, 6vw, 72px)',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 22,
        }}>What you want to ask</div>

        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(40px, 5.4vw, 64px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.08,
          letterSpacing: '-0.014em',
          margin: '0 0 clamp(22px, 2.4vw, 28px)',
          maxWidth: 880,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          {firstName
            ? <>{firstName}, the questions are <span style={{ fontStyle: 'italic', color: P.BR2 }}>yours.</span></>
            : <>The questions are <span style={{ fontStyle: 'italic', color: P.BR2 }}>yours.</span></>}
        </h1>

        <div style={{
          width: 'clamp(140px, 14vw, 180px)',
          height: 1,
          background: P.GO,
          margin: '0 auto clamp(22px, 2.4vw, 28px)',
        }} />

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.6vw, 19px)',
          fontStyle: 'italic',
          color: P.BR2,
          lineHeight: 1.65,
          maxWidth: 620,
          margin: '0 auto',
        }}>
          Edit them, reorder them, add your own, delete what does not matter.
        </p>
      </header>

      {/* ═════════ Body ═════════ */}
      <main style={{
        maxWidth: 840,
        margin: '0 auto',
        padding: 'clamp(48px, 6vw, 80px) clamp(24px, 3vw, 40px) clamp(64px, 8vw, 112px)',
      }}>

        {/* Empty state */}
        {questions.length === 0 && !generating && (
          <div style={{
            padding: 'clamp(48px, 6vw, 80px) clamp(28px, 3.4vw, 48px)',
            textAlign: 'center',
            background: 'rgba(232,221,196,0.4)',
            border: '1px dashed ' + P.SAND3,
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(18px, 1.8vw, 22px)',
              fontStyle: 'italic',
              color: P.BR2,
              lineHeight: 1.55,
              maxWidth: 480,
              margin: '0 auto clamp(28px, 3vw, 40px)',
            }}>
              You do not have any questions yet. Generate a starting list, or add your own below.
            </p>
            <BtnPrimary onClick={regenerate}>Generate starting list</BtnPrimary>
          </div>
        )}

        {/* Generating state */}
        {generating && (
          <div style={{
            padding: 'clamp(48px, 6vw, 80px) clamp(24px, 3vw, 40px)',
            textAlign: 'center',
          }}>
            <Spinner color={P.GO} size={48} />
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginTop: 32,
              marginBottom: 14,
            }}>Drafting</div>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(18px, 1.8vw, 22px)',
              fontStyle: 'italic',
              color: P.TX,
              lineHeight: 1.5,
              margin: 0,
            }}>Cleo is drafting your questions.</p>
          </div>
        )}

        {/* Question list */}
        {!generating && questions.map(function(q, i) {
          return (
            <QuestionRow
              key={q.id}
              question={q}
              index={i}
              total={questions.length}
              onUpdate={function(newText) { updateQuestionText(q.id, newText); }}
              onDelete={function() { deleteQuestion(q.id); }}
              onMoveUp={function() { moveUp(i); }}
              onMoveDown={function() { moveDown(i); }}
              onBlur={function() { saveQuestions(); }}
            />
          );
        })}

        {/* Add-new card */}
        {!generating && (
          <div style={{
            marginTop: 'clamp(28px, 3vw, 40px)',
            padding: 'clamp(24px, 2.8vw, 32px)',
            background: 'rgba(232,221,196,0.4)',
            border: '1px dashed ' + P.GO,
          }}>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 14,
            }}>Add your own</div>
            <textarea
              value={newQuestionText}
              onChange={function(e) { setNewQuestionText(e.target.value); }}
              placeholder={'Type a question in your own words\u2026'}
              rows={2}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.4vw, 17px)',
                color: P.INK,
                background: P.PAP,
                border: '1px solid ' + P.SAND3,
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
                lineHeight: 1.7,
                transition: 'border-color 0.2s',
              }}
              onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
              onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
              onKeyDown={function(e) {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addQuestion();
              }}
            />
            <div style={{
              marginTop: 14,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.TL,
              }}>Cmd or Ctrl, then Enter to add</div>
              <BtnGhost onClick={addQuestion} disabled={!newQuestionText.trim()}>
                Add to list
              </BtnGhost>
            </div>
          </div>
        )}

        {/* Save / Finalise bar */}
        {!generating && questions.length > 0 && (
          <div style={{
            marginTop: 'clamp(40px, 5vw, 64px)',
            padding: 'clamp(28px, 3.2vw, 40px)',
            background: P.OBS,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.GO2,
                marginBottom: 10,
              }}>Status</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.4vw, 17px)',
                fontStyle: 'italic',
                color: 'rgba(240,232,212,0.9)',
                lineHeight: 1.55,
              }}>
                {saving && 'Saving\u2026'}
                {!saving && lastSavedAt && isFinalized && (
                  <span>Finalised {formatDate(data.stage4_questions.finalizedAt)}. Your GP has your list.</span>
                )}
                {!saving && lastSavedAt && !isFinalized && (
                  <span>Draft saved. Finalise when you are ready.</span>
                )}
                {!saving && !lastSavedAt && 'Unsaved changes.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={function() { saveQuestions(); }}
                disabled={saving}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 28px',
                  background: 'transparent',
                  color: P.GO2,
                  border: '1px solid ' + P.GO2,
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={function(e) { if (!saving) e.currentTarget.style.background = 'rgba(210,184,134,0.1)'; }}
                onMouseLeave={function(e) { if (!saving) e.currentTarget.style.background = 'transparent'; }}>
                Save draft
              </button>
              <button
                onClick={function() { saveQuestions({ finalize: true }); }}
                disabled={saving || questions.length === 0}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 32px',
                  background: P.GO,
                  color: P.OBS,
                  border: 'none',
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'background 0.2s',
                }}
                onMouseEnter={function(e) { if (!saving) e.currentTarget.style.background = P.GO2; }}
                onMouseLeave={function(e) { if (!saving) e.currentTarget.style.background = P.GO; }}>
                Finalise for GP
              </button>
            </div>
          </div>
        )}

        {/* Regenerate link */}
        {!generating && questions.length > 0 && (
          <div style={{
            marginTop: 'clamp(28px, 3vw, 40px)',
            textAlign: 'center',
          }}>
            <button
              onClick={regenerate}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: P.BR2,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'color 0.2s',
              }}
              onMouseEnter={function(e) { e.currentTarget.style.color = P.BR; }}
              onMouseLeave={function(e) { e.currentTarget.style.color = P.BR2; }}>
              Regenerate AI suggestions
            </button>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(13px, 1.1vw, 14px)',
              fontStyle: 'italic',
              color: P.TF,
              marginTop: 10,
            }}>Replaces your current list with a fresh AI draft.</p>
          </div>
        )}

        {/* Back nav */}
        <div style={{
          marginTop: 'clamp(40px, 5vw, 64px)',
          textAlign: 'center',
        }}>
          <BtnGhost onClick={function() { if (onNavigate) onNavigate('results-reveal'); }}>
            Back to my findings
          </BtnGhost>
        </div>
      </main>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// QuestionRow — editable, reorderable, deletable
// ═════════════════════════════════════════════════════════════════════════
function QuestionRow({ question, index, total, onUpdate, onDelete, onMoveUp, onMoveDown, onBlur }) {
  var editingState = useState(false);
  var editing = editingState[0];
  var setEditing = editingState[1];

  var textState = useState(question.text);
  var text = textState[0];
  var setText = textState[1];

  useEffect(function() { setText(question.text); }, [question.text]);

  function commitEdit() {
    setEditing(false);
    if (text !== question.text) {
      onUpdate(text);
      if (onBlur) onBlur();
    }
  }

  return (
    <div style={{
      padding: 'clamp(20px, 2.4vw, 28px)',
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      borderLeft: '3px solid ' + P.GO,
      marginBottom: 12,
      display: 'grid',
      gridTemplateColumns: 'clamp(32px, 5vw, 48px) 1fr auto',
      gap: 'clamp(10px, 1.8vw, 22px)',
      alignItems: 'flex-start',
      transition: 'border-color 0.2s',
    }}>
      {/* Numeral */}
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(12px, 1vw, 14px)',
        fontWeight: 700,
        color: P.BR2,
        letterSpacing: '0.34em',
        paddingTop: 8,
      }}>{(index + 1).toString().padStart(2, '0')}</div>

      {/* Editable text */}
      <div>
        {editing ? (
          <textarea
            autoFocus
            value={text}
            onChange={function(e) { setText(e.target.value); }}
            onBlur={commitEdit}
            onKeyDown={function(e) {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
              if (e.key === 'Escape') { setText(question.text); setEditing(false); }
            }}
            rows={Math.max(2, Math.ceil(text.length / 70))}
            style={{
              width: '100%',
              padding: '10px 14px',
              fontFamily: FF,
              fontSize: 'clamp(16px, 1.6vw, 19px)',
              fontStyle: 'italic',
              color: P.INK,
              background: P.PAP,
              border: '1px solid ' + P.BR,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              lineHeight: 1.65,
            }}
          />
        ) : (
          <p
            onClick={function() { setEditing(true); }}
            style={{
              fontFamily: FF,
              fontSize: 'clamp(17px, 1.7vw, 21px)',
              fontStyle: 'italic',
              color: P.TX,
              lineHeight: 1.55,
              cursor: 'text',
              padding: '4px 0',
              margin: 0,
            }}>{question.text}</p>
        )}
        {question.category && !editing && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginTop: 14,
          }}>{question.category}</div>
        )}
      </div>

      {/* Reorder + delete controls */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}>
        <ControlBtn onClick={onMoveUp} disabled={index === 0} title="Move up">Up</ControlBtn>
        <ControlBtn onClick={onMoveDown} disabled={index === total - 1} title="Move down">Dn</ControlBtn>
        <ControlBtn onClick={onDelete} title="Delete" warning>Del</ControlBtn>
      </div>
    </div>
  );
}

// ───────── Control button (Cormorant 700 uppercase, themed) ─────────
function ControlBtn({ children, onClick, disabled, title, warning }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 38,
        height: 30,
        fontFamily: FF,
        fontSize: 'clamp(10px, 0.85vw, 11px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: warning ? P.RED : P.BR2,
        background: 'transparent',
        border: '1px solid ' + (warning ? P.RED : P.SAND3),
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.3 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        transition: 'all 0.2s',
      }}
      onMouseEnter={function(e) {
        if (!disabled) {
          e.currentTarget.style.background = warning ? 'rgba(192,57,43,0.06)' : 'rgba(201,169,97,0.08)';
        }
      }}
      onMouseLeave={function(e) {
        if (!disabled) e.currentTarget.style.background = 'transparent';
      }}>{children}</button>
  );
}
